import { HelperCore, uuidv4 } from '@/common/dependencies';
import { useEffect } from 'react';

const createBroadcast = (channelName: string) => {
  try {
    return new BroadcastChannel(channelName);
  } catch {
    const uniqueId = uuidv4().replace(/-/g, '');
    const storateKey = `${channelName}-msg`;
    // 降级到 localStorage 事件
    return {
      postMessage: (msg: {
        type: string;
        data: unknown;
        from?: string;
      }) => {
        localStorage.setItem(storateKey, JSON.stringify({
          ...msg,
          processId: uniqueId,
        }));
        localStorage.removeItem(storateKey);
      },
      addEventListener: (_: string, handler: (ev: MessageEvent) => void) => {
        window.addEventListener('storage', (e: StorageEvent) => {
          // 只接收广播事件
          if (e.key !== storateKey) return;
          // 只接收写入事件
          if (e.newValue === null) return;
          try {
            const { type, data, from, processId } = JSON.parse(e.newValue);
            // 自己发的自己不接收
            if (processId === uniqueId) return;
            handler(new MessageEvent('message', { data: { type, data, from } }));
          } catch {}
        });
      },
    };
  }
};

const broadCast = createBroadcast('bot.n.cn');
const broadcastHandler = {};

broadCast.addEventListener('message', (event: MessageEvent<{
  type: string;
  data: unknown;
  from?: string;
}>) => {
  if (!event.data) return;
  const { type, data, from } = event.data;
  if (from !== 'broadcast-channel') return;
  HelperCore.log('Received broadcast message:', type, data);
  broadcastHandler[type]?.forEach((fn) => {
    try {
      fn(data);
    } catch {
      // do nothing
    }
  });
});

/**
 * 注册监听
 * @param type 事件类型
 * @param cb 事件回调
 */
const on = (type: string, cb: (...args: any[]) => any = () => {}) => {
  broadcastHandler[type] = broadcastHandler[type] || [];
  broadcastHandler[type].push(cb);
};

/**
 * 移除监听
 * @param {String} type
 * @param {Function} cb
 * @example
 *  broadcast.off("topic_update", handler);
 */
const off = (type: string, cb?: (...args: any[]) => any) => {
  if (cb) {
    broadcastHandler[type] = broadcastHandler[type].filter(item => item != cb);
  } else {
    broadcastHandler[type] = [];
  }
};

export const useBroadcast = (type: string, handler: (...args: any[]) => any) => {
  useEffect(() => {
    on(type, handler);

    return () => {
      off(type, handler);
    };
  }, []);
}

export function sendBroadcast (type: string, data?: unknown) {
  HelperCore.log('Publish broadcast message:', type, data);
  broadCast.postMessage({
    from: 'broadcast-channel',
    type,
    data,
  });
}

// debug
declare global {
  interface Window {
    _broadcastStore: ReturnType<typeof createBroadcast>;
  }
}
window._broadcastStore = broadCast;
